import React, { useEffect, useState } from 'react';
import { Image, Button, Stack, Dropdown } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { dataLimits, S3_URL, USER_TYPES } from 'constants';
import {
	useBlockUserMutation,
	useDeleteFeedMutation,
	useFeedCommentsQuery,
	useFeedDetailsMutation,
	useFeedDetailsQuery,
	usePostLikeMutation,
	useReportMutation,
} from 'services';
import RenderMentions from 'components/SocialPost/RenderMentions';
import { getCreatedDifferenceInBestUnit, unitNumber } from 'utils';
import useAuthStore from 'store/auth';
import {
	removeFeedByPostId,
	removeFeedByUserId,
	setPostLikedUsers,
	updateIsConnected,
	updateIsLike,
	updateTotalComments,
	updateTotalLikes,
} from 'store/feeds';
import toast from 'react-hot-toast';
import useCommonStore, { setModalConfig } from 'store/common';
import FeedComments from './FeedComments/index.tsx';
import CommentForm from './FeedComments/CommentForm.tsx';
import PostPhotoGallery from 'components/PostPhotoGallery/index.tsx';
import useTranslate from 'hooks/useTranslate.ts';
import './styles.scss';

const SocialPostDetail: React.FC = () => {
	const { translate } = useTranslate();
	const { id } = useParams<any>();
	const navigate = useNavigate();
	const location = useLocation();
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const { mutateAsync: postLikeMutation, isPending } = usePostLikeMutation();
	const { mutateAsync: reportFeedsMutation } = useReportMutation();
	const { mutateAsync: blockUserMutation } = useBlockUserMutation();
	const { mutateAsync: deleteFeedMutation } = useDeleteFeedMutation();
	const { mutateAsync: feedDetailsMutation } = useFeedDetailsMutation();

	const [currentPage, setCurrentPage] = useState(1);

	const searchString = `${id}?order=DESC&page=${currentPage}&limit=${dataLimits?.feedComments}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const { data: { docs: commentList = [], meta = {} } = {}, refetch } = useFeedCommentsQuery({
		searchString,
		id,
	});

	const {
		data: feedDetails = {},
		status,
		isPending: isPendingDetails,
		refetch: refetchFeed,
		isError,
	} = useFeedDetailsQuery(Number(id));

	useEffect(() => {
		if (isError) navigate('/social-feed-posts');
	}, [isError]);

	const {
		postId,
		isLike,
		profilePicture,
		totalComments,
		totalLikes,
		userId,
		userLocation,
		userName,
		created,
		content,
		taggedUsers,
		hashTags,
		mediaItems = [],
		userType,
	} = feedDetails;

	const [isLiked, setIsLiked] = useState(false);
	const [numberOfLikes, setNumberOfLikes] = useState(0);
	const [totalNumberOfComments, setTotalNumberOfComments] = useState(0);
	const [commentListArr, setCommentListArr] = useState<string[]>([]);

	useEffect(() => {
		refetch();
	}, [currentPage]);

	useEffect(() => {
		const timer = setTimeout(() => {
			const hash = location?.hash;
			if (hash) {
				const element = document.getElementById(hash.substring(1));
				if (element) {
					element.scrollIntoView({ behavior: 'smooth' });
				}
			} else {
				window.scrollTo({
					top: 0,
					behavior: 'instant',
				});
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [location?.hash, location?.pathname]);

	useEffect(() => {
		setCommentListArr((prev: any) => [...prev, ...commentList]);

		return () => {
			setCommentListArr([]);
		};
	}, [commentList]);

	const removeComments = (commentId: number) => {
		setCommentListArr(commentListArr?.filter((data: any) => data?.commentId !== commentId));

		feedDetailsMutation(postId)
			.then((res) => {
				if (res?.success && res?.data) {
					setTotalNumberOfComments(res.data.totalComments || 0);
				}
			})
			.catch((error) => {
				console.error('Failed to fetch feed details:', error);
			});
	};

	useEffect(() => {
		if (status === 'success' && !isPendingDetails) {
			setIsLiked(isLike ? true : false);
			setNumberOfLikes(totalLikes);
			updateTotalLikes(postId, Number(totalLikes) || 0);
			updateTotalComments(postId, totalComments);
			setTotalNumberOfComments(totalComments);
		}
	}, [feedDetails, isPendingDetails, status]);

	useEffect(() => {
		updateTotalComments(postId, totalNumberOfComments);
	}, [totalNumberOfComments]);

	useEffect(() => {
		setNumberOfLikes(totalLikes);
	}, []);

	const onHandleLike = () => {
		postLikeMutation({ postId: id })
			.then((res: any) => {
				if (res?.success) {
					const updateNumberOfLikes = isLiked
						? Number(numberOfLikes) > 0
							? numberOfLikes - 1
							: 0
						: Number(numberOfLikes) + 1;
					updateIsLike(id, !isLiked);
					updateTotalLikes(postId, updateNumberOfLikes);
					setIsLiked(!isLiked);
					setNumberOfLikes(updateNumberOfLikes);
				} else {
					toast.error(res?.message);
				}
			})
			.catch((error) => {
				console.error('Failed to like post:', error);
			});
	};

	const handleShow = () => {
		if (numberOfLikes > 0) {
			setModalConfig({
				visible: true,
				id: postId,
				type: 'postLikedBy',
				onClose: onCloseLikeModal,
				props: {
					size: 'lg',
					dialogClassName: 'dialog-like-list',
				},
			});
		}
	};

	const onCloseLikeModal = () => {
		setPostLikedUsers([]);
		hideCommonModal();
	};

	const onAddNewComment = (newData: any) => {
		setCommentListArr((prev: any) => [newData, ...prev]);
	};

	const handleReport = () => {
		setModalConfig({
			visible: true,
			id: Number(postId),
			onClick: handleModalClick,
			type: 'reportFeeds',
			contentType: 'socialPost',
		});
	};

	const handleModalClick = (payload: any) => {
		reportFeedsMutation(payload)
			.then((res: any) => {
				if (res?.success) {
					removeFeedByPostId(postId);
				}
			})
			.finally(() => {
				setModalConfig({
					visible: false,
					id: postId,
					onClick: handleModalClick,
					type: 'reportFeeds',
					contentType: 'socialPost',
				});
				navigate('/social-feed-posts');
			});
	};

	const blockProfile = () => {
		blockUserMutation(userId).then((res: any) => {
			if (res?.success) {
				removeFeedByUserId(userId);
				toast.success(translate('ToastMessage.userBlockedSuccessfully'));
				navigate('/social-feed-posts');
			}
		});
	};

	const blockUserModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: blockProfile,
			data: {
				title: translate('Modal.wantToBlockAccount'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleDelete = (id: number | null = null) => {
		setModalConfig({
			visible: true,
			id: id,
			type: 'confirmModal',
			onClick: () => onHandleYes(id),
			data: {
				title: translate('Modal.wantToDelete', { title: `${translate('Modal.thisFeed')}` }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleYes = (id: number | null) => {
		deleteFeedMutation(id)
			.then((res: any) => {
				if (res?.success) {
					removeFeedByPostId(Number(id));
					navigate(-1);
				} else {
					toast.error(res?.message);
				}
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	const onAfterEdited = () => {
		refetchFeed();
		hideCommonModal();
	};

	const redirectToProfile = () => {
		navigate(`/${userType === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/profile/${userId}`);
	};

	return (
		<>
			<div style={{ cursor: 'pointer' }} className="back-link" onClick={() => navigate(-1)}>
				<i className="ri-arrow-left-s-line"></i> {translate('Social.back')}
			</div>
			<div className="social-post white-box bg-white">
				<Stack className="social-post-inner">
					<div className="social-post-header position-relative d-flex justify-content-between align-items-center">
						<div className="post-profile d-flex align-items-md-center gap-3">
							<div className="post-profile-img rounded-circle" onClick={() => redirectToProfile()}>
								<Image
									src={profilePicture ? `${S3_URL}${profilePicture}` : IMAGE_PATHS.UserBG}
									className="w-100 h-100 object-fit-cover rounded-circle"
									loading="lazy"
								/>
							</div>
							<div>
								<h5 onClick={() => redirectToProfile()} className="fw-bold mb-1">
									{userName}
								</h5>
								<div className="post-profile-detail d-flex flex-wrap">
									<p className="mb-0">{userLocation}</p>
									{created && <p className="mb-0">{getCreatedDifferenceInBestUnit(created)}</p>}
								</div>
							</div>
						</div>
						<div className="post-profile-actions d-flex gap-2 gap-xxl-3">
							<div className="edit-action">
								<Dropdown>
									<Dropdown.Toggle
										variant="success"
										id="dropdown-basic"
										className="p-0 rounded-circle"
									>
										<i className="ri-more-2-fill"></i>
									</Dropdown.Toggle>

									<Dropdown.Menu>
										{userId !== user?.userId ? (
											<>
												<Dropdown.Item onClick={handleReport}>
													<i className="ri-flag-line"></i> {translate('Report.report')}
												</Dropdown.Item>
												<Dropdown.Item onClick={blockUserModal}>
													<i className="ri-spam-2-line"></i> {translate('Report.block')}
												</Dropdown.Item>
											</>
										) : (
											<>
												<Dropdown.Item
													onClick={() => {
														setModalConfig({
															visible: true,
															id: postId,
															type: 'createNewPost',
															onClose: onAfterEdited,
															props: {
																size: 'lg',
															},
														});
													}}
												>
													<i className="ri-edit-line"></i> {translate('Common.edit')}
												</Dropdown.Item>
												<Dropdown.Item onClick={() => onHandleDelete(postId)}>
													<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
												</Dropdown.Item>
											</>
										)}
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
					</div>
					<div className="social-post-body">
						{content && (
							<div className="post-content">
								<p>
									<RenderMentions content={content} taggedUsers={taggedUsers} hashTags={hashTags} />
								</p>
							</div>
						)}

						{mediaItems?.length > 0 && <PostPhotoGallery mediaItems={mediaItems} />}
					</div>
					<div className="social-post-actions d-flex gap-2 gap-sm-3">
						<Button
							className="gray-transparent-btn post-like-btn rounded-pill"
							onClick={() => onHandleLike()}
							disabled={isPending}
						>
							<i className={`ri-heart-3-${isLiked ? 'fill' : 'line'} me-0`}></i>
						</Button>
						<Button className="gray-transparent-btn rounded-pill" onClick={() => handleShow()}>
							{unitNumber(numberOfLikes, 0)} {translate('Social.likes')}
						</Button>
						<Button className="gray-transparent-btn rounded-pill">
							<i className="ri-chat-3-line"></i> {unitNumber(totalNumberOfComments, 0)}{' '}
							{translate('Social.comments')}
						</Button>
						<Button
							className="gray-transparent-btn rounded-pill"
							onClick={() => {
								setModalConfig({
									visible: true,
									id: Number(postId),
									type: 'postShare',
								});
							}}
						>
							<i className="ri-share-forward-line"></i> {translate('Social.share')}
						</Button>
					</div>

					<div id="comment-section">
						<CommentForm
							feedDetails={feedDetails}
							setTotalNumberOfComments={setTotalNumberOfComments}
							onAddNewComment={onAddNewComment}
						/>
					</div>
					{commentListArr?.length > 0 && (
						<div className="comments-list">
							{commentListArr?.map((comment: any, index: number) => (
								<div key={index}>
									<FeedComments
										comment={comment}
										setTotalNumberOfComments={setTotalNumberOfComments}
										removeComments={removeComments}
									/>
								</div>
							))}
							{meta?.hasNextPage && (
								<div
									className="text-center mt-3"
									onClick={() => setCurrentPage((prev: any) => Number(prev) + 1)}
								>
									<Button className="view-reply-btn border-0 p-0 bg-transparent">
										{translate('Button.seeMore')}
									</Button>
								</div>
							)}
						</div>
					)}
				</Stack>
			</div>
		</>
	);
};

export default SocialPostDetail;
